import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Minus, Plus, Trash2, ShoppingBag, ArrowRight, Tag, 
  Percent, ChevronRight, Shield, Truck, RotateCcw, X, ArrowLeft, MessageCircle
} from 'lucide-react';

// WhatsApp business number
const WHATSAPP_NUMBER = '971503847865';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, cartSubtotal, shippingFee, cartTotal, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, discount, description}
  const [promoLoading, setPromoLoading] = useState(false);
  const [availableCodes, setAvailableCodes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/promocodes`)
      .then((res) => setAvailableCodes(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promocodes/validate`, { code: promoCode.trim(), subtotal: cartSubtotal });
      setAppliedPromo(res.data);
      toast.success(`Promo code "${res.data.code}" applied!`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    toast.success('Promo code removed');
  };

  const calculateDiscount = () => appliedPromo?.discount || 0;

  const discount = calculateDiscount();
  const finalTotal = cartSubtotal - discount + shippingFee;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-4"
        >
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-12 h-12 text-slate-400" />
          </div>
          <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-2">Your cart is empty</h2>
          <p className="text-slate-500 mb-6">Looks like you haven't added anything to your cart yet</p>
          <Button 
            onClick={() => navigate('/products')}
            className="bg-[#0047AB] hover:bg-[#003380]"
            data-testid="browse-products-btn"
          >
            Browse Products
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 md:py-10">
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 text-slate-600 hover:text-slate-900 -ml-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-[#0047AB]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-900 font-medium">Shopping Cart</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Cart Items */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h1 className="text-xl font-outfit font-bold text-slate-900">
                  Shopping Cart ({cart.length} {cart.length === 1 ? 'item' : 'items'})
                </h1>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    if (window.confirm('Clear all items from cart?')) {
                      clearCart();
                      toast.success('Cart cleared');
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>

              {/* Items List */}
              <div className="divide-y divide-slate-100">
                <AnimatePresence>
                  {cart.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="p-4 md:p-6"
                      data-testid={`cart-item-${item.id}`}
                    >
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <Link to={`/products/${item.id}`} className="flex-shrink-0">
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-xl border border-slate-200"
                          />
                        </Link>

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                            <div>
                              <Link 
                                to={`/products/${item.id}`}
                                className="font-outfit font-semibold text-slate-900 hover:text-[#0047AB] line-clamp-2"
                              >
                                {item.name}
                              </Link>
                              {item.category && (
                                <p className="text-sm text-slate-500 mt-1">{item.category}</p>
                              )}
                            </div>
                            <p className="text-lg font-bold text-[#0047AB] md:text-right whitespace-nowrap">
                              ₹{(item.price * item.quantity).toLocaleString()}
                            </p>
                          </div>

                          {/* Price per unit */}
                          <p className="text-sm text-slate-500 mt-1">
                            ₹{item.price.toLocaleString()} × {item.quantity}
                          </p>

                          {/* Quantity Controls */}
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 rounded-lg"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                data-testid={`decrease-qty-${item.id}`}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="w-10 text-center font-semibold text-slate-900">
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 rounded-lg"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                data-testid={`increase-qty-${item.id}`}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>

                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                removeFromCart(item.id);
                                toast.success('Item removed from cart');
                              }}
                              data-testid={`remove-item-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Truck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Free Delivery</p>
                  <p className="text-xs text-slate-500">On orders above ₹1,500</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Secure Payment</p>
                  <p className="text-xs text-slate-500">100% secure checkout</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Easy Returns</p>
                  <p className="text-xs text-slate-500">7-day return policy</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:w-96">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sticky top-24">
              <h2 className="text-lg font-outfit font-bold text-slate-900 mb-4">Order Summary</h2>

              {/* Promo Code */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Promo Code
                </label>
                {appliedPromo ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <p className="font-semibold text-green-700">{appliedPromo.code}</p>
                      <p className="text-xs text-green-600">{appliedPromo.description}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-green-700 hover:bg-green-100"
                      onClick={handleRemovePromo}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="flex-1"
                      data-testid="promo-input"
                    />
                    <Button
                      variant="outline"
                      onClick={handleApplyPromo}
                      disabled={promoLoading}
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? '...' : 'Apply'}
                    </Button>
                  </div>
                )}
                
                {/* Available Codes */}
                {availableCodes.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-500 mb-2">Available codes:</p>
                    <div className="flex flex-wrap gap-2">
                      {availableCodes.map((p) => (
                        <button
                          key={p.code}
                          onClick={() => setPromoCode(p.code)}
                          title={p.description}
                          className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded-md transition-colors"
                          data-testid={`available-code-${p.code}`}
                        >
                          {p.code}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal ({cart.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                  <span>₹{cartSubtotal.toLocaleString()}</span>
                </div>
                
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <Percent className="w-4 h-4" />
                      Discount
                    </span>
                    <span>-₹{discount.toLocaleString()}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-slate-600">
                  <span>Shipping</span>
                  <span className={shippingFee === 0 ? 'text-green-600 font-medium' : ''}>
                    {shippingFee === 0 ? 'Free' : `₹${shippingFee.toLocaleString()}`}
                  </span>
                </div>
                
                {shippingFee > 0 && (
                  <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded-lg">
                    Shipping is based on the highest fee among your cart items
                  </p>
                )}

                <div className="border-t border-slate-200 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-outfit font-bold text-slate-900">Total</span>
                    <span className="text-2xl font-outfit font-bold text-[#0047AB]" data-testid="cart-grand-total">
                      ₹{finalTotal.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Checkout Buttons */}
              <div className="mt-6 space-y-3">
                <Button
                  className="w-full h-12 bg-[#0047AB] hover:bg-[#003380] text-white shadow-lg shadow-blue-900/20"
                  onClick={() => navigate('/checkout')}
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                {/* WhatsApp Checkout CTA */}
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                    `Hi DXB2BOM! I'd like to place an order:\n\n${cart.map(item => `• ${item.name} x ${item.quantity} = ₹${(item.price * item.quantity).toLocaleString()}`).join('\n')}\n\nTotal: ₹${finalTotal.toLocaleString()}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full h-12 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors shadow-lg shadow-green-900/20"
                  data-testid="whatsapp-checkout-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Order via WhatsApp
                </a>
                
                {!isAuthenticated && (
                  <p className="text-xs text-center text-slate-500">
                    You can checkout as guest or{' '}
                    <Link to="/otp-login" className="text-[#0047AB] hover:underline">
                      login
                    </Link>
                    {' '}for a faster experience
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
